import React, { useState } from 'react';
import Image from 'next/image';
import styles from './combined-shopping-module.module.scss';
import { StyledSelect } from './styled-select/styled-select';
import { Button } from '@nerdwallet/currency';
import {
  ACTION_OPTIONS,
  ANALYTICS_SCHEMA,
  COMBINED_SHOPPING_MODULE_CONTENT,
  COMBINED_SHOPPING_MODULE_CTA_LINKS,
  VERTICAL_OPTIONS,
} from './constants';
import { ActionOption, VerticalOption, VerticalsEnum } from './types';
import { InsuranceFlow } from './quiz-flows/insurance-flow';
import { MortgageFlow } from './quiz-flows/mortgage-flow';
import SegmentImpression from '../../segment-impression';
import useAnalytics from '~/app/lib/segment/useAnalytics';
import FolderTabContainer from '../folder-tab-container/folder-tab-container';

export const CombinedShoppingModule = (): JSX.Element => {
  const [insuranceFlow, setInsuranceFlow] = useState(false);
  const [mortgageFlow, setMortgageFlow] = useState(false);
  const [vertical, setVertical] = useState<VerticalOption>(VERTICAL_OPTIONS[0]);
  const [action, setAction] = useState<ActionOption>(ACTION_OPTIONS[0]);
  const { trackSegmentTrackEvent } = useAnalytics();

  const sourceContent = COMBINED_SHOPPING_MODULE_CONTENT[vertical.value];
  const sourceDesktopImage = sourceContent.images?.desktopURL || '';
  const sourceMobileImage = sourceContent.images?.mobileURL || '';
  const sourceAlt = sourceContent.images?.alt || '';

  const handleCtaClick = () => {
    trackSegmentTrackEvent('Element Interaction', {
      entity_name: ANALYTICS_SCHEMA[vertical.value][action.value],
    });

    if (vertical.value === VerticalsEnum.Insurance) {
      setInsuranceFlow(true);
      return;
    }

    if (vertical.value === VerticalsEnum.Mortgages) {
      setMortgageFlow(true);
      return;
    }

    const link =
      COMBINED_SHOPPING_MODULE_CTA_LINKS[vertical.value][action.value];

    handleOpenLink(link);
  };

  const handleOpenLink = (link: string) => {
    if (!link) return;

    const newWindow = window.open(link, '_blank');
    if (newWindow) {
      newWindow.focus();
    }
  };

  const resetQuiz = () => {
    setInsuranceFlow(false);
    setMortgageFlow(false);
  };

  if (insuranceFlow) {
    return (
      <div className={styles.container}>
        <InsuranceFlow
          action={action}
          desktopSourceImage={sourceDesktopImage}
          mobileSourceImage={sourceMobileImage}
          sourceAlt={sourceAlt}
          handleOpenLink={handleOpenLink}
          resetQuiz={resetQuiz}
        />
      </div>
    );
  }

  if (mortgageFlow) {
    return (
      <div className={styles.container}>
        <MortgageFlow
          action={action}
          desktopSourceImage={sourceDesktopImage}
          mobileSourceImage={sourceMobileImage}
          sourceAlt={sourceAlt}
          handleOpenLink={handleOpenLink}
          resetQuiz={resetQuiz}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <SegmentImpression
        eventName="Element Impression"
        eventProps={{
          entity_name: 'hp_shopping_quiz_l1',
        }}
      >
        {/* Desktop */}
        <div className={styles.desktopContainer}>
          <FolderTabContainer
            tabBackgroundColor="#e3faf2"
            contentBackgroundColor="#e3faf2"
            roundedCorners
            showBorderBottom
          >
            <div className={styles.combinedWrapper}>
              <div className={styles.combinedForm}>
                <span className={styles.formText}>I’m looking for a top</span>
                <StyledSelect
                  selectOptions={VERTICAL_OPTIONS}
                  selectedValue={vertical}
                  setSelectedValue={setVertical}
                />
                {vertical.hasTwoDropdowns && (
                  <>
                    <span className={styles.formText}>and I want to</span>
                    <StyledSelect
                      selectOptions={ACTION_OPTIONS}
                      selectedValue={action}
                      setSelectedValue={setAction}
                    />
                  </>
                )}
                <div className={styles.ctaContainer}>
                  <Button primary block onClick={handleCtaClick}>
                    LET’S GO
                  </Button>
                </div>
              </div>
              <div className={styles.desktopTabletImage}>
                <Image
                  src={sourceDesktopImage}
                  alt={sourceAlt}
                  width={462}
                  height={393}
                />
              </div>
            </div>
          </FolderTabContainer>
        </div>
        {/* Mobile and Tablet */}
        <div className={styles.mobileTabletContainer}>
          <div className={styles.combinedWrapper}>
            <div className={styles.combinedForm}>
              <span className={styles.formText}>I’m looking for a top</span>
              <StyledSelect
                selectOptions={VERTICAL_OPTIONS}
                selectedValue={vertical}
                setSelectedValue={setVertical}
              />
              {vertical.hasTwoDropdowns && (
                <>
                  <span className={styles.formText}>and I want to</span>
                  <StyledSelect
                    selectOptions={ACTION_OPTIONS}
                    selectedValue={action}
                    setSelectedValue={setAction}
                  />
                </>
              )}
              <div className={styles.ctaContainer}>
                <Button primary block onClick={handleCtaClick}>
                  LET’S GO
                </Button>
              </div>
            </div>
            <div className={styles.desktopTabletImage}>
              <Image
                src={sourceDesktopImage}
                alt={sourceAlt}
                width={462}
                height={393}
              />
            </div>
          </div>
          <div className={styles.mobileImage}>
            <Image
              src={sourceMobileImage}
              alt={sourceAlt}
              width={686}
              height={360}
            />
          </div>
        </div>
      </SegmentImpression>
    </div>
  );
};
